import { useCallback, useReducer, useEffect, useRef } from "react"
import { calculateMinWidth } from "src/utils/table-render-functions"
import dayjs from "dayjs"
import { DATE_FORMAT } from "src/utils/constants"
import { formatCpfCnpj } from "src/utils/format-cpf-cnpj"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
    openModal: false,
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        case "SET_MODAL":
            return { ...state, openModal: action.payload }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

const getCetipConsulta = (cetipIsin, dadosEmpresa) => {
    if (dadosEmpresa?.CETIP) return dadosEmpresa.CETIP
    if (dadosEmpresa?.cetip) return dadosEmpresa.cetip

    if (cetipIsin?.CETIP) return cetipIsin.CETIP
    if (cetipIsin?.cetip) return cetipIsin.cetip

    if (typeof cetipIsin === "string") return cetipIsin

    return ""
}

export const useUltimoEventoCreditoDevedor = (
    emissaoService,
    cpfCnpj,
    dadosEmpresa,
    cetipIsin
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const cetipConsulta = getCetipConsulta(cetipIsin, dadosEmpresa)

    const loadedRef = useRef(cpfCnpj)

    useEffect(() => {
        if (loadedRef.current === cpfCnpj) return
        loadedRef.current = cpfCnpj

        // limpa tudo se o CPF mudar
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cpfCnpj])

    const buildTable = useCallback(() => {
        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `Razão Social: ${dadosEmpresa.Devedor || "N/A"}`,
                        `CNPJ: ${formatCpfCnpj(dadosEmpresa.CPF_CNPJ_Devedor) || "N/A"}`,
                        `Grupo Econômico: ${dadosEmpresa.GrupoEconomico || "N/A"}`,
                    ],
                },
            ],
        })
    }, [dadosEmpresa])

    const loadTableRows = useCallback(async () => {
        // Se não tiver CPF, avisa o usuário e para.
        if (!cpfCnpj) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CPFCNPJ não informado.", 
                    type: "warning",
                },
            })
            // Garante que o modal abre (para ver o alerta) mas sem dados
            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_TABLE", payload: [] })
            dispatch({ type: "SET_MODAL", payload: true }) 
            return
        }

        buildTable()
        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.eventoCreditoDevedor(
                "get",
                `?CPFCNPJ=${cpfCnpj.toUpperCase()}`
            )
            const { results } = response.body

            if (!results || results.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CPF/CNPJ consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                const rows = ordenaPorDataReferencia(results, "desc").map(
                    (obj, index) => ({ ...obj, id: index })
                )
                dispatch({ type: "SET_ROWS", payload: rows })
            }
            
            // Abre o modal após carregar
            dispatch({ type: "SET_MODAL", payload: true })

        } catch (error) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao buscar dados ou CPF/CNPJ não encontrado.",
                    type: "warning", 
                },
            })
            dispatch({ type: "SET_ROWS", payload: [] })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cpfCnpj, emissaoService, buildTable])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.eventoCreditoDevedor("delete", ID)
                await loadTableRows()
            } catch (error) {
                 dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]
        

        if (!lastItem) {
            lastItem = {
                DataReferencia: dayjs().format(DATE_FORMAT),
                DataValidade: dayjs().add(1, 'year').format(DATE_FORMAT), 
                CETIP: cetipConsulta, 
                CPFCNPJ: cpfCnpj,
            }
        }
        console.log("--- DEBUG: Objeto a ser enviado (POST):", lastItem)

        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.eventoCreditoDevedor("post", {
                ...lastItem,
                CETIP: lastItem?.CETIP ?? cetipConsulta,
            })
            console.log("--- DEBUG: Sucesso no POST. Recarregando tabela... ---")
            await loadTableRows()
        } catch(error) {
            console.error("--- DEBUG: Erro no POST ---", error)
             dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipConsulta, cpfCnpj, emissaoService, loadTableRows])

    const updateRow = useCallback(
        async (newRow) => {
            const { ID } = newRow
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.eventoCreditoDevedor("patch", ID, newRow)
            } catch(error) {
                 dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
            return newRow
        },
        [emissaoService]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )
    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthEvento:
                calculateMinWidth(state.rows, "EventoCreditoDevedor") / 2,
        },
        actions: {
            loadTableRows,
            deleteRow,
            addRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
            closeModal: () => dispatch({ type: "SET_MODAL", payload: false }),
        },
    }
}